import { useEventContextV2, usePermissionsContext } from "~/app/providers";
import { isVideo } from "~/utils/images";
import Image from "next/image";
import Link from "next/link";
import { FaTrash } from "react-icons/fa";
import { If } from "~/components/v3/Conditional";
import { RiImageAddLine } from "react-icons/ri";
import { RiFileVideoFill } from "react-icons/ri";
import { env } from "~/env.mjs";
import { Button } from "~/components/v3/themed/Button";
import { PiUploadSimpleBold } from "react-icons/pi";
import { AcceptType } from "~/utils/images";
import { useRef, useState } from "react";
import UploadImage from "~/components/file/UploadImage";
import { deleteImage } from "~/components/v3/events-actions/images";
import { toast } from "react-toastify";
import { attachImage } from "~/components/v3/events-actions/images";
import { getRandomVibeImage, getVibeImage } from "~/utils/cities";
import { useSession } from "next-auth/react";
import { Prisma } from "@prisma/client";
import ImageLightbox from "./ImageLightbox";
import { cn } from "~/utils/fontsConfig";
import { CustomizableLayout } from "~/types/CustomizableLayout";

type UserImage = Prisma.UserImageGetPayload<{
    select: { id: true; name: true; userId: true };
}>;

export const ShowEventImages = ({
    ref,
    onSuccessfulUpload,
    customization,
}: {
    ref: React.RefObject<HTMLHeadingElement | null>;
    onSuccessfulUpload: () => void;
    customization?: CustomizableLayout & {
        sideBackgroundClassName?: string;
    };
}) => {
    const event = useEventContextV2();
    const permissions = usePermissionsContext();
    const session = useSession();
    const isAttendee = event.attendees.some(
        (attendee) => attendee.user.id === session.data?.user.id,
    );

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { image } = getRandomVibeImage(event.slug);
    const [activeUrl, setActiveUrl] = useState<string | null>(null);

    const onUploadComplete = async (filename: string) => {
        // add to db for the event
        await attachImage(event.slug, filename);
        onSuccessfulUpload();
    };

    const canDeleteImage = (image: UserImage) => {
        if (permissions.isManager || permissions.isAdmin) return true;
        if (session.data?.user.id === image.userId) return true;
        return false;
    };

    const handleDeleteImage = async (image: UserImage) => {
        toast.loading("Deleting image", { toastId: "delete-image" });
        await deleteImage(event.slug, image.id);
        toast.dismiss("delete-image");
        toast.success("Image deleted!");
    };

    // do not show anything (upload or view) if user is not an attendee
    if (
        !permissions.isHost &&
        !permissions.isManager &&
        !permissions.isAdmin &&
        !isAttendee
    ) {
        return false;
    }

    return (
        <>
            <h4
                className={cn(
                    "text-xl text-v3-N100",
                    customization?.labelClassName,
                )}
                ref={ref}
            >
                Event Photos{" "}
                {!!event.userImages.length && `(${event.userImages.length})`}
            </h4>
            <div
                className={cn(
                    "whitespace-pre-wrap text-base text-v3-N-300",
                    customization?.descriptionClassName,
                )}
            >
                <p
                    className={cn(
                        "break-words",
                        customization?.descriptionClassName,
                    )}
                >
                    Have some photos from the event? Share them here with other
                    attendees and community members!
                </p>
            </div>
            <ul
                className={
                    "mt-4 grid w-full auto-cols-fr gap-3 md:gap-6 2xl:gap-3"
                }
                style={{
                    gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`,
                }}
            >
                {event.userImages.map((image) => (
                    <li
                        key={image.name}
                        className={cn(
                            "relative h-[150px] rounded-lg bg-v3-N500",
                            customization?.className,
                        )}
                    >
                        <Link
                            onClick={(e) => {
                                e.preventDefault();
                                setActiveUrl(
                                    `${env.NEXT_PUBLIC_ASSETS_URL}/event/${event.id}/${image.name}`,
                                );
                            }}
                            href={`${env.NEXT_PUBLIC_ASSETS_URL}/event/${event.id}/${image.name}`}
                            className={customization?.linkClassName}
                        >
                            <If condition={isVideo(image.name)}>
                                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 transform flex-col items-center text-center">
                                    <RiFileVideoFill size={42} />
                                    {image.name}
                                </div>
                            </If>
                            <If condition={!isVideo(image.name)}>
                                <Image
                                    className={"rounded-xl object-cover"}
                                    objectFit="cover"
                                    fill={true}
                                    alt={"event image"}
                                    src={`${env.NEXT_PUBLIC_ASSETS_URL}/event/${event.id}/${image.name}`}
                                />
                            </If>
                        </Link>
                        {canDeleteImage(image) && (
                            <Button
                                className="absolute right-2 top-2 h-8 w-8 p-0 hover:cursor-pointer hover:text-black"
                                variant="tertiary"
                                onClick={() => handleDeleteImage(image)}
                            >
                                <FaTrash size={14} />
                            </Button>
                        )}
                    </li>
                ))}
                <li
                    className="group relative h-[150px] cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Image
                        className={"rounded-xl object-cover grayscale"}
                        fill={true}
                        alt={"event image placeholder"}
                        src={image}
                    />
                    <div
                        className={cn(
                            "absolute inset-0 rounded-xl bg-v3-N500 opacity-95 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100",
                            customization?.sideBackgroundClassName,
                        )}
                    >
                        <div
                            className={cn(
                                "flex h-full flex-col items-center justify-center gap-2 text-center text-sm",
                                customization?.descriptionClassName,
                            )}
                        >
                            <RiImageAddLine
                                className="h-6 w-6"
                                aria-hidden="true"
                            />
                            Share your event photos and videos!
                            <Button
                                className={cn(
                                    "mt-2 flex flex-row items-center gap-2 text-sm",
                                    customization?.buttons?.secondary,
                                )}
                                variant="secondary"
                            >
                                <PiUploadSimpleBold
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                                Upload media
                            </Button>
                            <UploadImage
                                className="hidden"
                                ref={fileInputRef}
                                variant="button"
                                multi={true}
                                bucketKey={`event/${event.id}`}
                                onUploadComplete={onUploadComplete}
                                accept={[AcceptType.VIDEO, AcceptType.IMAGE]}
                                showProgress={true}
                                maxFileSize={100 * 1024 * 1024}
                            />
                        </div>
                    </div>
                </li>
            </ul>
            <ImageLightbox
                urls={event.userImages.map(
                    (image) =>
                        `${env.NEXT_PUBLIC_ASSETS_URL}/event/${event.id}/${image.name}`,
                )}
                activeUrl={activeUrl}
            />
        </>
    );
};
